import React, { useState } from 'react';
import {
  SalesIntelligence,
  CallPhaseAnalysis,
  ToneScore,
  getPhaseLabel,
  getBuyingSignalColor,
} from '../adherence/types';
import ManagerReviewPanel from '../adherence/ManagerReviewPanel';
import { ProgressBar, scoreTone, toneTextClass } from '../ui/ProgressBar';
import type { CallInsightsAnalysis } from '../adherence/types';

interface SpeechRateAlert {
  total_words: number;
  words_per_minute: number;
  timestamp: string;
}

interface SalesIntelligenceV2SectionProps {
  sessionId: string;
  intelligence: SalesIntelligence;
  callInsights: CallInsightsAnalysis;
  speechRateAlerts: SpeechRateAlert[];
  isAdmin: boolean;
  onReviewSaved: () => void;
  onNavigateToTimestamp?: (ts: string) => void;
}

interface ToneBarRowProps {
  label: string;
  toneScore: ToneScore;
  isPositive: boolean;
}

const ToneBarRow: React.FC<ToneBarRowProps> = ({ label, toneScore, isPositive }) => {
  // Same thresholds as the score badges; a high negative quality is the problem.
  const tone = scoreTone(toneScore.score, isPositive);
  const textColor = toneTextClass(tone);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-ink-secondary">{label}</span>
        <div className="flex items-center gap-1.5">
          <span className={`text-xs font-bold ${textColor}`}>{toneScore.score}%</span>
          <span className="text-[10px] text-ink-muted">(AI confidence: {toneScore.confidence_level}%)</span>
        </div>
      </div>
      <ProgressBar value={toneScore.score} tone={tone} />
      {toneScore.explanation && (
        <p className="text-[11px] text-ink-muted leading-tight">{toneScore.explanation}</p>
      )}
    </div>
  );
};

const SalesIntelligenceV2Section: React.FC<SalesIntelligenceV2SectionProps> = ({
  sessionId,
  intelligence,
  callInsights,
  speechRateAlerts,
  isAdmin,
  onReviewSaved,
  onNavigateToTimestamp,
}) => {
  const [expandedPhase, setExpandedPhase] = useState<number | null>(null);

  if (!intelligence) {
    return (
      <div className="text-center py-12 text-ink-muted">
        No sales intelligence data available for this session.
      </div>
    );
  }

  const phases = intelligence?.phases ?? [];

  const togglePhase = (idx: number) => {
    setExpandedPhase((prev) => (prev === idx ? null : idx));
  };

  return (
    <div className="space-y-5">
      {/* 1. Summary bar */}
      <div className="bg-white border border-hairline rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-ink-muted mb-1">Overall Session Customer Buying Signal</p>
            <span
              className={`inline-block text-sm font-semibold px-3 py-1 rounded capitalize ${getBuyingSignalColor(
                intelligence?.overall_session_signal ?? ''
              )}`}
            >
              {intelligence?.overall_session_signal?.replace(/_/g, ' ') ?? 'Unknown'}
            </span>
            {typeof intelligence?.overall_session_signal_score === 'number' && (
              <span className="ml-2 text-xs text-ink-muted">
                {Math.round(intelligence.overall_session_signal_score)}%
              </span>
            )}
          </div>
          <div>
            <p className="text-xs text-ink-muted mb-1">Closing Customer Buying Signal</p>
            <span
              className={`inline-block text-sm font-semibold px-3 py-1 rounded capitalize ${getBuyingSignalColor(
                intelligence?.closing_signal ?? ''
              )}`}
            >
              {intelligence?.closing_signal?.replace(/_/g, ' ') ?? 'Unknown'}
            </span>
            {typeof intelligence?.closing_signal_score === 'number' && (
              <span className="ml-2 text-xs text-ink-muted">
                {Math.round(intelligence.closing_signal_score)}%
              </span>
            )}
          </div>
        </div>
        {intelligence?.phase_narrative && (
          <p className="text-sm text-ink-secondary">{intelligence.phase_narrative}</p>
        )}
      </div>

      {/* 2. Phase detail cards */}
      {phases.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-ink-secondary">Call Phase Details ({phases.length})</p>
          {phases.map((phase: CallPhaseAnalysis, idx: number) => {
            const isExpanded = expandedPhase === idx;

            return (
              <div key={idx} className="border border-hairline rounded-lg overflow-hidden">
                {/* Phase header */}
                <button
                  onClick={() => togglePhase(idx)}
                  className="w-full flex items-start justify-between px-4 py-3 bg-surface-subtle hover:bg-surface-sunken transition-colors text-left"
                  type="button"
                >
                  <div className="space-y-1.5">
                    <p className="text-sm font-semibold text-ink">{getPhaseLabel(phase.phase)}</p>
                    <div className="space-y-0.5 text-xs text-ink-secondary">
                      <p>
                        <span className="font-medium text-ink-muted">Customer Buying Signal:</span>{' '}
                        <span className="capitalize">{phase.buying_signal?.replace(/_/g, ' ')}</span>
                        {typeof phase.buying_signal_score === 'number' && (
                          <span className="text-ink-muted ml-1">({phase.buying_signal_score}%)</span>
                        )}
                      </p>
                      <p>
                        <span className="font-medium text-ink-muted">Customer Emotional Tone:</span>{' '}
                        <span className="capitalize">{phase.emotional_tone?.replace(/_/g, ' ')}</span>
                        {typeof phase.emotional_tone_score === 'number' && (
                          <span className="text-ink-muted ml-1">({phase.emotional_tone_score}%)</span>
                        )}
                      </p>
                      {phase.conversation_control && (
                        <p>
                          <span className="font-medium text-ink-muted">Conversation Control:</span>{' '}
                          <span className="capitalize">{phase.conversation_control?.replace(/_/g, ' ')}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <span
                    className="text-ink-muted text-xs transition-transform duration-150 flex-shrink-0 mt-1"
                    style={{
                      transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                      display: 'inline-block',
                    }}
                  >
                    &#9660;
                  </span>
                </button>

                {/* Phase body */}
                {isExpanded && (
                  <div className="p-4 border-t border-hairline space-y-3">
                    {phase.trigger && (
                      <div className="bg-info-tint border border-info rounded p-2">
                        <span className="text-xs font-semibold text-info uppercase">Trigger / Reason: </span>
                        <span className="text-sm text-ink">{phase.trigger}</span>
                      </div>
                    )}

                    {phase.phase_summary && (
                      <p className="text-sm text-ink-secondary">{phase.phase_summary}</p>
                    )}

                    {phase.start_approx && (
                      <p className="text-xs text-ink-muted">
                        {phase.start_approx}
                        {phase.end_approx ? ` - ${phase.end_approx}` : ''}
                      </p>
                    )}

                    {/* View in Timeline */}
                    {(phase.primary_timestamp || phase.supporting_quotes?.[0]?.timestamp_approx) && (
                      <button
                        type="button"
                        onClick={() => onNavigateToTimestamp?.(
                          phase.primary_timestamp ?? phase.supporting_quotes![0].timestamp_approx!
                        )}
                        className="text-xs text-signature hover:text-signature-strong font-medium underline"
                      >
                        View in Timeline &rarr;
                      </button>
                    )}

                    {/* What worked / didn't work */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {phase.what_worked && (
                        <div className="bg-success-tint border border-success rounded p-3">
                          <p className="text-xs font-semibold text-success uppercase mb-1">
                            What Worked
                          </p>
                          <p className="text-sm text-ink">{phase.what_worked}</p>
                        </div>
                      )}
                      {phase.what_didnt_work && (
                        <div className="bg-error-tint border border-error rounded p-3">
                          <p className="text-xs font-semibold text-error uppercase mb-1">
                            What Could Improve
                          </p>
                          <p className="text-sm text-ink">{phase.what_didnt_work}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 4. Deal Risk Signals */}
      {intelligence?.deal_risk_signals && intelligence.deal_risk_signals.length > 0 && (
        <div className="bg-white border border-hairline rounded-lg p-4">
          <p className="text-sm font-semibold text-error mb-2">Deal Risk Signals</p>
          <div className="flex flex-wrap gap-2">
            {intelligence.deal_risk_signals.map((risk, idx) => (
              <span
                key={idx}
                className="text-xs bg-error-tint text-error border border-error px-2.5 py-1 rounded-full"
              >
                {risk}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 5. Rapport Moments */}
      {intelligence?.rapport_moments && intelligence.rapport_moments.length > 0 && (
        <div className="bg-white border border-hairline rounded-lg p-4">
          <p className="text-sm font-semibold text-ink-secondary mb-3">Rapport Moments</p>
          <div className="space-y-2">
            {intelligence.rapport_moments.map((rapportMoment, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm text-ink-secondary">
                {rapportMoment.timestamp_approx && (
                  <span className="text-xs text-ink-muted flex-shrink-0 mt-0.5">
                    {rapportMoment.timestamp_approx}
                  </span>
                )}
                <p>{rapportMoment.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 6. Speech Rate Assessment */}
      {(intelligence?.speech_rate_assessment || speechRateAlerts.length > 0) && (
        <div className="bg-white border border-hairline rounded-lg p-4">
          <p className="text-sm font-semibold text-ink-secondary mb-2">Speech Rate Assessment</p>
          {intelligence?.speech_rate_assessment && (
            <p className="text-sm text-ink-secondary mb-2">{intelligence.speech_rate_assessment}</p>
          )}
          {speechRateAlerts.length > 0 && (
            <p className="text-xs text-ink-muted">
              {speechRateAlerts.length} speech rate alert{speechRateAlerts.length !== 1 ? 's' : ''} during the call
            </p>
          )}
        </div>
      )}

      {/* 7. Rep Tone Analysis */}
      {callInsights?.tone_analysis?.positive && (
        <div className="bg-white border border-hairline rounded-lg p-4 space-y-4">
          <p className="text-sm font-semibold text-ink-secondary">Rep Tone Analysis</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-success-tint border border-success rounded-lg p-3 space-y-3">
              <p className="text-[10px] font-semibold text-success uppercase tracking-wider">Positive</p>
              <ToneBarRow label="Confidence" toneScore={callInsights.tone_analysis.positive.confidence} isPositive={true} />
              <ToneBarRow label="Optimism" toneScore={callInsights.tone_analysis.positive.optimism} isPositive={true} />
            </div>
            <div className="bg-error-tint border border-error rounded-lg p-3 space-y-3">
              <p className="text-[10px] font-semibold text-error uppercase tracking-wider">Negative</p>
              <ToneBarRow label="Defensiveness" toneScore={callInsights.tone_analysis.negative.defensiveness} isPositive={false} />
              <ToneBarRow label="Confrontation" toneScore={callInsights.tone_analysis.negative.confrontation} isPositive={false} />
            </div>
          </div>
          {callInsights.tone_analysis.overall_coaching && (
            <p className="text-xs text-ink-secondary border-t border-hairline pt-3">
              {callInsights.tone_analysis.overall_coaching}
            </p>
          )}
        </div>
      )}

      {/* 8. Coaching: This Client */}
      {intelligence?.coaching_for_this_client && intelligence.coaching_for_this_client.length > 0 && (
        <div className="bg-info-tint border border-info rounded-lg p-4">
          <p className="text-sm font-semibold text-info mb-2">Coaching: This Client</p>
          <ol className="space-y-1 list-decimal list-inside">
            {intelligence.coaching_for_this_client.map((step, idx) => (
              <li key={idx} className="text-sm text-ink">
                {step}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* 9. Coaching: General Improvement */}
      {intelligence?.coaching_for_general_improvement &&
        intelligence.coaching_for_general_improvement.length > 0 && (
          <div className="bg-info-tint border border-info rounded-lg p-4">
            <p className="text-sm font-semibold text-info mb-2">Coaching: General Improvement</p>
            <ol className="space-y-1 list-decimal list-inside">
              {intelligence.coaching_for_general_improvement.map((tip, idx) => (
                <li key={idx} className="text-sm text-ink">
                  {tip}
                </li>
              ))}
            </ol>
          </div>
        )}

      {/* 10. Manager Review */}
      <ManagerReviewPanel
        sessionId={sessionId}
        currentSection="sales_intelligence"
        managerReview={callInsights?.manager_review}
        isAdmin={isAdmin}
        onReviewSaved={onReviewSaved}
      />
    </div>
  );
};

export default SalesIntelligenceV2Section;
